import { useEffect, useState, useRef, useCallback } from "react";
import clsx from "clsx";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/nl";
import { adjustTime, getPublicImageUrl } from "../helpers/helpers";
import Icon from "./Icon";

import NotificationIcon from "../assets/new-image/Notification.svg";
import NewsLetterImg from "../assets/image svg/newsletter.svg";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectUser } from "../slices/userReducer";
import { supabase } from "../supabaseClient";

dayjs.extend(utc);
dayjs.extend(timezone);

const Carousel = ({ onlineEvents, formattedCurrentDate }) => {
  const [firstModal, setFirstModal] = useState(false);
  const [secondModal, setSecondModal] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [subscribeObj, setSubscribeObj] = useState({
    Email: "",
    event: "",
    event_id: "",
    token: "",
  });
  const [userToken, setUserToken] = useState("");
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const user = useSelector(selectUser);

  useEffect(() => {
    if (user.email) {
      setSubscribeObj({
        ...subscribeObj,
        Email: user.email,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const tokenFromCookie = localStorage.getItem("token");
    setUserToken(tokenFromCookie);
  }, []);

  const { t } = useTranslation();

  useEffect(() => {
    const closeOnOutsideClick = (e) => {
      if (!e.target.closest(".modal-content")) {
        closeModalHandler();
      }
    };
    document.addEventListener("click", closeOnOutsideClick);
    return () => {
      document.removeEventListener("click", closeOnOutsideClick);
    };
  }, []);

  const checkScrollButtons = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      const maxScroll = container.scrollWidth - container.clientWidth;
      setCanScrollRight(container.scrollLeft < maxScroll - 1);
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollButtons);
      checkScrollButtons();
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScrollButtons);
      }
    };
  }, [checkScrollButtons]);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = container.offsetWidth * 0.8;
      const newScrollPosition =
        container.scrollLeft +
        (direction === "right" ? scrollAmount : -scrollAmount);
      container.scrollTo({
        left: newScrollPosition,
        behavior: "smooth",
      });
    }
  };

  if (onlineEvents.length < 1) {
    return null;
  }


  const sortedByDate = onlineEvents
    .filter((event) => {
      const registerFrom = adjustTime(event.signup_from);
      return formattedCurrentDate < registerFrom;
    })
    .sort((a, b) => {
      return dayjs(a.datetime).valueOf() - dayjs(b.datetime).valueOf();
    })
    .map((event) => {
      const dateObject = dayjs(event.datetime).locale("nl");
      return {
        ...event,
        fulldate: {
          date: dateObject.format("D MMM"),
          time: dateObject.format("HH:mm"),
        },
      };
    });

  const getEventData = (eventItem) => {
    const eventName = eventItem.name;
    const eventId = eventItem.id;
    setSubscribeObj((prevState) => ({
      ...prevState,
      event: eventName,
      event_id: eventId,
      token: userToken,
    }));
  };

  const openFirstModal = (e, item) => {
    e.stopPropagation();
    getEventData(item);
    setFirstModal(true);
  };

  const validateEmail = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValidEmail = emailRegex.test(subscribeObj.Email);
    if (isValidEmail) {
      if (user.user_info_id) {
        // Create a TicketAlert record in supabase
        const { data, error } = await supabase.from("TicketAlert").insert({
          event_id: subscribeObj.event_id,
          user_id: user.user_info_id,
        });

        if (error) {
          console.error("Error creating TicketAlert record:", error);
        } else {
          console.log("TicketAlert record created successfully:", data);
        }
        setEmailError(false);
        closeModalHandler();
        setSecondModal(true);
      } else {
        // The user is not logged in, so we need to create an anonymous record in supabase
        const { data, error } = await supabase.from("TicketAlert").insert({
          event_id: subscribeObj.event_id,
          email: subscribeObj.Email,
        });

        if (error) {
          console.error("Error creating TicketAlert record:", error);
        } else {
          console.log("TicketAlert record created successfully:", data);
        }
        setEmailError(false);
        closeModalHandler();
        setSecondModal(true);
      }
    } else {
      setEmailError(true);
    }
  };

  const closeModalHandler = () => {
    setFirstModal(false);
    setEmailError(false);
  };

  return (
    <section className="relative lg:mb-8 max-lg:px-[16px]">
      <div className="flex justify-between items-center pb-[14px]">
        <div className="">
          <h2 className="text-[22px] text-white lg:text-[24px] text-start leading-[26px] lg:leading-[36px]">
            {t("coming_soon")}
          </h2>
          <p className="text-neutral-400">{t("want_to_be_reminded")}</p>
        </div>

        {/* Navigation Buttons */}
        {sortedByDate.length > 1 && (
          <div className="flex items-center gap-5 lg:mr-7">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className={`p-2 rounded-full shadow-lg transition-all duration-200 bg-white
                ${
                  canScrollLeft
                    ? "hover:bg-gray-50 lg:cursor-pointer opacity-100"
                    : "cursor-not-allowed opacity-40"
                }`}
              aria-label="Scroll left"
            >
              <Icon
                icon="heroicons:chevron-left"
                className="w-[20px] h-[20px] text-gray-800"
              />
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className={`p-2 rounded-full shadow-lg transition-all duration-200 bg-white
                ${
                  canScrollRight
                    ? "hover:bg-gray-50 lg:cursor-pointer opacity-100"
                    : "cursor-not-allowed opacity-40"
                }`}
              aria-label="Scroll right"
            >
              <Icon
                icon="heroicons:chevron-right"
                className="w-[20px] h-[20px] text-gray-800"
              />
            </button>
          </div>
        )}
      </div>

      {/* Scroll Container */}
      <div
        ref={scrollContainerRef}
        className="flex gap-6 w-full overflow-x-auto scroll-smooth hide-scrollbar"
        style={{
          scrollSnapType: "x mandatory",
          touchAction: "auto",
          overflowY: "visible",
        }}
      >
        {sortedByDate.map((item, index) => (
          <button
            onClick={(e) => openFirstModal(e, item)}
            key={index}
            className="flex-none py-1 lg:pl-2 w-full md:w-[calc(33.33%-16px)] text-start scroll-snap-align-start"
            style={{
              scrollSnapAlign: "start",
              userSelect: "auto",
              pointerEvents: "auto",
              touchAction: "auto",
            }}
          >
            <div className="relative bg-gradient-to-r from-[#AD7AEF]/[0.06] via-[#C2A5FF]/[0.4] to-[#554B6A]/[0.1855] max-lg:!p-[16px] border border-[#AD7AEF] text-white transition-all duration-300 event-content">
              <div className="mb-4 rounded-[6px] lg:rounded-[8px] w-full max-w-[95vw] max-h-[430px] overflow-hidden">
                <img
                  src={getPublicImageUrl(item.event_image_filename)}
                  alt="Event"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="top-[25px] lg:top-[32px] right-[25px] lg:right-[28px] z-10 absolute flex items-center gap-2 bg-[#F7F2FD] shadow p-1 px-2 py-1 rounded-[6px] text-[#7721E5]">
                <img
                  src={NotificationIcon}
                  alt="notification"
                  className="w-[16px] h-[16px]"
                />
                <div className="bg-transparent font-bold text-[14px]">
                  {t("notify_me")}
                </div>
              </div>
              <div>
                <h5 className="font-[700] text-[16px] text-white lg:text-[18px] truncate leading-[22px] lg:leading-[24px] elipsis">
                  {item.name.charAt(0).toUpperCase() +
                    item.name.slice(1).toLowerCase().substring(0, 24)}
                  {item.name.length > 24 ? "..." : ""}
                </h5>
                <h6 className="font-normal !text-white text-[14px] lg:text-[16px] truncate leading-[16px] elipsis">
                  {item.fulldate.time} • {item.venue.name} • {item.city}
                </h6>
                <div className="flex justify-start items-center gap-2 mt-[14px]">
                  <div className="bg-green-600 !rounded-[4px] text-white">
                    <span className="flex justify-center items-center !bg-transparent !px-[6px] !py-[4px] min-w-[55px] !text-[14px]">
                      {item.currency}
                      {parseFloat(item.our_price).toFixed(2)}
                    </span>
                  </div>
                  {parseFloat(item.original_price) > 0 && (
                    <div className="bg-gray-600 !rounded-[4px] text-white">
                      <span className="flex justify-center items-center !bg-transparent !px-[6px] !py-[4px] min-w-[55px] !text-[14px] line-through">
                        {item.currency}
                        {parseFloat(item.original_price).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* First Modal */}
      <div className={clsx("modal firstModal", firstModal ? "open" : "")} id="">
        <div
          className={`modal-content ${
            user.email
              ? "max-lg:!top-[calc(100%-240px)]"
              : "max-lg:!top-[calc(100%-400px)]"
          }`}
        >
          <div className="header">
            <span
              className="closeSecondModal close-button"
              onClick={closeModalHandler}
            >
              &times;
            </span>
            <h2>{t("keep_me_updated")}</h2>
          </div>
          <div className="Modal-Body">
            {!user.email && (
              <>
                <p>{t("email-address")}</p>
                <input
                  value={subscribeObj.Email}
                  onChange={(e) =>
                    setSubscribeObj({ ...subscribeObj, Email: e.target.value })
                  }
                  id="modalEmailInput"
                  type="email"
                  name="email"
                  placeholder="E-mail"
                />
                {emailError ? (
                  <p className="!text-[14px] modal-error">
                    {t("invalid_email")}
                  </p>
                ) : (
                  <p className="sturen">{t("email_send_once_event_is_live")}</p>
                )}
              </>
            )}
            <button
              className="openSecondModal Stuur"
              type="button"
              onClick={(e) => validateEmail()}
            >
              {t("keep_me_updated")}
            </button>
          </div>
        </div>
      </div>

      {/* Second Modal */}
      <div
        className={clsx("modal secondModal", secondModal ? "open" : "")}
        id=""
      >
        <div className="max-lg:!top-[calc(100%-470px)] modal-content">
          <div className="header">
            <span
              className="closeSecondModal close-button"
              onClick={() => setSecondModal(false)}
            >
              &times;
            </span>
            <h2>Hou me op de hoogte</h2>
          </div>
          <div className="Modal-Body">
            <div className="flex justify-center thnaks-img">
              <img src={NewsLetterImg} alt="newletter" className="thanks" />
            </div>
            <p className="!text-center gelukt">{t("all_good")}</p>
            <button
              className="openSecondModal Stuur"
              type="submit"
              onClick={() => setSecondModal(false)}
            >
              Terug naar home
            </button>
          </div>
        </div>
      </div>

      {/* Add this CSS to your global styles */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        /* Fix for mobile vertical scrolling */
        @media (max-width: 768px) {
          .event-content {
            touch-action: auto !important;
            pointer-events: auto !important;
            -webkit-overflow-scrolling: touch !important;
          }

          /* Ensure the body and html can scroll */
          html, body {
            touch-action: auto !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch !important;
          }

          /* Make sure the container doesn't block vertical scrolling */
          .w-full.flex.overflow-x-auto {
            touch-action: pan-x pan-y !important;
            -webkit-overflow-scrolling: touch !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Carousel;
